import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axiosInstance from '../api/axios';

export const getThumbnailUrl = (uri) => {
    if (!uri) return null;
    const base = String(axiosInstance.defaults.baseURL || '').replace(/\/$/, '');
    return `${base}/fsthumbnails/${encodeURIComponent(uri)}`;
};

const GlassSpinner = () => (
    <div
        className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/35 dark:bg-gray-900/40 backdrop-blur-md"
        role="status"
        aria-label="Loading thumbnail"
    >
        <div className="relative h-12 w-12">
            <div className="absolute inset-0 rounded-full border-2 border-white/50 dark:border-white/15" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#41A6D7] border-r-[#41A6D7]/70 animate-spin" />
        </div>
        <span className="sr-only">Loading thumbnail</span>
    </div>
);

const CardThumbnail = ({ icon, title, imageSrc, embedSrc, objectFit = 'cover', onPlay, playLabel = 'Play' }) => {
    const hasMedia = Boolean(imageSrc || embedSrc);
    const [status, setStatus] = useState(hasMedia ? 'loading' : 'idle');
    const imageRef = useRef(null);

    useEffect(() => {
        if (!imageSrc && !embedSrc) {
            setStatus('idle');
            return;
        }
        setStatus('loading');
        const image = imageRef.current;
        if (image && imageSrc && image.complete && image.naturalWidth > 0) {
            setStatus('ready');
        }
    }, [imageSrc, embedSrc]);

    const isReady = status === 'ready';
    const isLoading = status === 'loading';
    const fitClass = objectFit === 'contain' ? 'object-contain' : 'object-cover';

    return (
        <div className="relative w-full h-36 overflow-hidden rounded-lg mb-3 mt-8 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
            <FontAwesomeIcon
                icon={icon}
                className={`w-32 h-32 text-gray-500 dark:text-gray-400 transition-opacity duration-300 ${isReady ? 'opacity-0' : 'opacity-100'}`}
            />

            {isLoading && <GlassSpinner />}

            {imageSrc && !embedSrc ? (
                <img
                    ref={imageRef}
                    src={imageSrc}
                    alt={title}
                    decoding="async"
                    className={`absolute inset-0 w-full h-full ${fitClass} bg-gray-100 dark:bg-gray-800 transform hover:scale-105 transition-all duration-300 ${
                        isReady ? 'opacity-100' : 'opacity-0'
                    }`}
                    onLoad={() => setStatus('ready')}
                    onError={() => setStatus('idle')}
                />
            ) : null}

            {embedSrc ? (
                <iframe
                    src={embedSrc}
                    title={title}
                    className={`absolute inset-0 w-full h-full border-0 transition-opacity duration-300 ${isReady ? 'opacity-100' : 'opacity-0'}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    onLoad={() => setStatus('ready')}
                />
            ) : null}

            {onPlay ? (
                <button
                    type="button"
                    onClick={onPlay}
                    className="absolute inset-0 z-20 flex items-center justify-center bg-black/20 hover:bg-black/35 transition-colors"
                    aria-label={playLabel}
                >
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#41A6D7] text-white shadow-lg ring-4 ring-white/40">
                        <svg viewBox="0 0 24 24" className="ml-0.5 h-5 w-5 fill-current" aria-hidden="true">
                            <path d="M8 5.14v13.72L19 12 8 5.14z" />
                        </svg>
                    </span>
                </button>
            ) : null}
        </div>
    );
};

export default CardThumbnail;
